/**
 * Как распределять свободный остаток.
 *
 * Выбор показывается рублями и сроками, а не процентами: «25 % в цели»
 * ничего не говорит человеку о его жизни, а «свободные траты 5 000 ₽, подушка будет
 * готова примерно в январе» — говорит.
 *
 * Три правила экрана:
 *
 * · **Считает ядро, на настоящих данных.** Суммы и сроки берутся из тех же
 *   функций, что и главная; подписи здесь ничего не вычисляют.
 * · **Нет остатка — нет выбора.** Три одинаковых варианта по нулю рублей
 *   выглядят как насмешка; вместо них честная строка о том, когда режим
 *   начнёт влиять.
 * · **Сроков не обещаем.** Если дату закрытия цели посчитать не из чего,
 *   она не называется вовсе.
 */
import { useState } from 'react';
import {
  ZERO, MODE_TITLE, DEFAULT_MODE, add, clampPos, formatMoney, modeChoice, proposeAllocation,
  recommendedMode, sub,
  type Money, type PlanMode,
} from '@skoka/core';
import { SheetWrap } from './sheet.js';
import { Tip } from './tip.js';
import { dayMonthYear } from './text.js';
import { planInput, setPlanMode, type AppState } from './store.js';
import { Icon } from './icon.js';

export function ModeSheet({ state, onClose, onSaved, onRaiseGoal }: {
  state: AppState;
  onClose: () => void;
  /** Режим сохранён: наверху решают, предлагать ли пересчёт раскладки. */
  onSaved: (mode: PlanMode) => void;
  /** Поднять взнос в цель: отдельное решение, а не побочный эффект режима. */
  onRaiseGoal?: () => void;
}) {
  const current = state.planMode ?? DEFAULT_MODE;
  const [picked, setPicked] = useState<PlanMode>(current);
  const [busy, setBusy] = useState(false);

  /*
    Остаток третьего слоя один на все режимы: защищённые платежи, обычная
    жизнь и перенос от выбора не зависят. Берём его из предложения раскладки,
    посчитанного при нынешнем режиме, — режим на этот остаток не влияет.
  */
  const proposal = proposeAllocation(planInput(state));
  const rest = add(
    proposal.lines.want,
    ...proposal.lines.goals.map((g) => g.amount),
    proposal.lines.unallocated,
  );

  /** Сколько по каждой цели ещё не внесено в этом месяце. */
  const month = state.today.slice(0, 7);
  const remaining: Record<string, Money> = {};
  for (const g of state.goals) {
    const funded = add(...state.goalEvents
      .filter((e) => e.goalId === g.id && e.kind === 'contribution' && e.date.startsWith(month))
      .map((e) => e.amount));
    remaining[g.id] = clampPos(sub(g.monthlyContribution, funded));
  }

  const choice = modeChoice({ today: state.today, rest, goals: state.goals, remaining });
  /*
    Рекомендация — только с объяснением из данных человека.

    Постоянный бейдж «рекомендуем» у «Баланса» был неправдой: это значение
    по умолчанию, а не совет, посчитанный по чьей-то жизни.
  */
  const advice = recommendedMode({
    goals: state.goals,
    obligationsCovered: proposal.shortfall <= ZERO,
    accelerates: choice.accelerates,
  });

  return (
    <SheetWrap title="Как распределять свободный остаток" onClose={onClose}
      tip={rest <= ZERO || !choice.hasGoals ? undefined
        : !choice.accelerates
          ? 'Выбор режима сейчас цели не ускорит: поднять месячный взнос можно '
            + 'только отдельным решением.'
          : 'Сами платежи и жизнь от выбора не зависят.'}>
      {rest <= ZERO ? (
        <div className="note" style={{ marginTop: 0 }}>
          После обязательных платежей и обычной жизни свободного остатка пока
          нет. Режим начнёт влиять, когда он появится.
        </div>
      ) : !choice.hasGoals ? (
        /*
          Целей нет или они собраны. Режимы целей показывать не за что:
          `goals_first` без цели — обещание в пустоту.
        */
        <div className="note" style={{ marginTop: 0 }}>
          Целей, которым нужны деньги в этом месяце, нет. Остаток
          {' '}{formatMoney(rest)} можно оставить нераспределённым, выделить
          свободные траты или завести цель — тогда появится и выбор режима.
        </div>
      ) : !choice.accelerates ? (
        /*
          Все цели ограничены принятым взносом. Три карточки с одинаковой
          суммой целей — не выбор пути, а имитация выбора.
        */
        <>
          <div className="note" style={{ marginTop: 0 }}>
            Цели уже получают весь принятый взнос — {formatMoney(choice.committed)}.
          </div>
          <div className="row"><div className="rl">Свободный остаток</div>
            <div className="rv num">{formatMoney(rest)}</div></div>
          <div className="row"><div className="rl">Идёт в цели по договору</div>
            <div className="rv num">{formatMoney(choice.committed)}</div></div>
          <div className="row"><div className="rl">Остаётся решить
            <Tip>Что с ним делать: оставить нераспределённым, направить свободные траты или
              увеличить взнос в цель — тогда покажу новую дату её закрытия.</Tip></div>
            <div className="rv num">{formatMoney(sub(rest, choice.committed))}</div></div>
          {onRaiseGoal && (
            <button className="btn" onClick={onRaiseGoal}>Увеличить взнос в цель</button>
          )}
        </>
      ) : (
        <>
          <div className="note" style={{ marginTop: 0 }}>
            Делится только то, что осталось после платежей, обычной жизни и
            переноса — {formatMoney(rest)}.
          </div>
          {advice && (
            <div className="note">
              {MODE_TITLE[advice.mode]} подходит сейчас: {advice.why}.
            </div>
          )}

          {choice.outlooks.map((o) => (
            <button key={o.mode} className={`li w${picked === o.mode ? ' on' : ''}`}
              onClick={() => setPicked(o.mode)}>
              <div className="nm">
                <b>{MODE_TITLE[o.mode]}{o.mode === DEFAULT_MODE ? ' · по умолчанию' : ''}</b>
                <span>
                  {/* Все три части остатка — всегда: иначе сумма не сходится с ним. */}
                  Свободные траты {formatMoney(o.self)} · в цели {formatMoney(o.goals)}
                  {` · не распределено ${formatMoney(o.unallocated)}`}
                </span>
                {o.byGoal.filter((g) => g.amount > ZERO || g.why).map((g) => (
                  <span className="rn" key={g.goalId}>
                    {g.name}: {g.amount > ZERO ? formatMoney(g.amount) : '—'}
                    {/* Дата держится на том, что взнос повторится: так и говорим. */}
                    {g.amount > ZERO && g.eta ? ` · будет готова примерно ${dayMonthYear(g.eta)}, если откладывать столько каждый месяц` : ''}
                    {g.why ? ` · ${g.why}` : ''}
                  </span>
                ))}
                {o.self === ZERO && (
                  <span className="rn">
                    На свободные траты не остаётся ничего — посмотри, что можно изменить
                  </span>
                )}
              </div>
              <div className="rt"><Icon name="chevron-right" size={13} /></div>
            </button>
          ))}
        </>
      )}

      <button className="btn pri" disabled={busy || picked === current}
        onClick={async () => {
          setBusy(true);
          await setPlanMode(picked);
          onSaved(picked);
        }}>
        {busy ? 'Сохраняю…' : 'Выбрать этот режим'}
      </button>
      <button className="btn" onClick={onClose}>Оставить как есть</button>
    </SheetWrap>
  );
}
